<template>
  <div v-if="isLoading" class="admin-company-rates__preloader">
    <vpreloader />
  </div>

  <div v-else class="admin-company-rates">
    <div v-if="form !== null">
      <div class="admin-company-rates__title">Выберите текущий тариф</div>

      <div class="admin-company-rates__block">
        <trigger
          v-for="tariff in tariffs"
          :key="tariff.id"
          :value="tariff.id === form.tariff_id"
          :text="tariff.name"
          :disabled="tariff.name === 'trial' || tariff.name === 'free'"
          class="admin-company-rates__checkbox"
          small
          @input="changeTariff(tariff.id)"
        />
      </div>

      <div class="admin-company-rates__block">
        <div class="admin-company-rates__title">Срок действия</div>

        <div class="admin-company-rates__block-inner">
          <date-range-picker
            ref="picker"
            v-model="dateRange"
            :locale-data="{ firstDay: 1, format: 'dd-mm-yyyy' }"
            show-dropdowns
          >
            <!-- @update="saveTariff()" -->
            <template #input="picker">
              {{ $moment(picker.startDate).format('YYYY-MM-DD') }} - {{ $moment(picker.endDate).format('YYYY-MM-DD') }}
            </template>
          </date-range-picker>
        </div>

        <div class="admin-company-rates__block-inner">
          Дата предыдущего списания: {{ $moment(form.last_refill_date).format('DD-MM-YYYY') }}
        </div>
        <div class="admin-company-rates__block-inner">
          Дата следующего списания: {{ $moment(form.next_refill_date).format('DD-MM-YYYY') }}
        </div>
      </div>

      <div class="admin-company-rates__block">
        <div class="admin-company-rates__title">Доступные действия</div>

        <trigger
          v-model="form.survey"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Анкета"
          small
        />

        <trigger
          v-model="form.basic_test"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Тестирование"
          small
        />

        <trigger
          v-model="form.extended_test"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Расширенное тестирование"
          small
        />

        <trigger
          v-model="form.videointerview"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Видеоинтервью"
          small
        />

        <trigger
          v-model="form.survey_screening"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Отсекающая анкета"
          small
        />

        <trigger
          v-model="form.integration"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Свой этап (интеграция)"
          small
        />

        <trigger
          v-model="form.action_history"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="История событий"
          small
        />

        <trigger
          v-model="form.vacancy_permissions"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Настройка прав на вакансию"
          small
        />

        <trigger
          v-model="form.custom_agreements"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Документы компании"
          small
        />

        <trigger
          v-model="form.api"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Открытое API"
          small
        />

        <trigger
          v-model="form.chatbot"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Чатбот"
          small
        />

        <trigger
          v-model="form.competences"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Компетенции"
          small
        />

        <trigger
          v-model="form.emotions"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Распознавание речи и эмоций"
          small
        />

        <trigger
          v-model="form.password_policy"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Управление парольной политикой"
          small
        />

        <trigger
          v-model="form.personal_manager"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Персональный менеджер"
          small
        />

        <trigger
          v-model="form.analytics"
          :disabled="!isCurrentTariff('enterprise')"
          class="admin-company-rates__checkbox"
          text="Аналитика"
          small
        />
      </div>

      <div class="admin-company-rates__block">
        <div class="admin-company-rates__title">Sms</div>

        <trigger v-model="form.is_sms_enabled" class="admin-company-rates__checkbox" text="Включить SMS" small />

        <transition name="fade">
          <trigger
            v-show="form.is_sms_enabled"
            v-model="form.is_sms_unlimited"
            class="admin-company-rates__checkbox"
            text="Безлимитные SMS"
            small
          />
        </transition>

        <transition name="fade">
          <div v-show="form.is_sms_enabled && !form.is_sms_unlimited" class="admin-company-rates__block-inner">
            Доступно
            <vcv-billing-counter v-model="form.sms_balance" :min-value="1" :max-value="20000" :step="10" />
          </div>
        </transition>
      </div>

      <div class="admin-company-rates__block">
        <div class="admin-company-rates__title">Количество вакансий</div>

        <trigger v-model="isVacanciesUnlimited" class="admin-company-rates__checkbox" text="Безлимит" small />

        <transition name="fade">
          <div v-show="!isVacanciesUnlimited" class="admin-company-rates__block-inner">
            Количество
            <vcv-billing-counter
              :value="form.vacancy_limit"
              :min-value="1"
              :max-value="1000"
              @input="changeVacancyLimit"
            />
            Текущий остаток: {{ form.vacancyCount }}
          </div>
        </transition>
      </div>

      <div class="admin-company-rates__block">
        <div class="admin-company-rates__title">Количество откликов</div>

        <trigger
          v-if="isCurrentTariff('professional') || isCurrentTariff('enterprise')"
          v-model="isResponsesUnlimited"
          class="admin-company-rates__checkbox"
          text="Безлимит"
          small
        />

        <transition-group name="fade">
          <div
            v-show="
              !isResponsesUnlimited &&
              (isCurrentTariff('startup') ||
                isCurrentTariff('business') ||
                isCurrentTariff('trial') ||
                isCurrentTariff('free'))
            "
            :key="0"
            class="admin-company-rates__block-inner"
          >
            <trigger
              v-model="isMonthlyResponseEnabled"
              class="admin-company-rates__checkbox"
              small
              text="Разблокировать"
            />

            Отклики тарифа

            <vcv-billing-counter
              :value="form.monthly_response_limit || 0"
              :min-value="0"
              :max-value="1000000"
              :disabled="!isMonthlyResponseEnabled"
              @input="form.monthly_response_limit = $event"
            />

            Текущий остаток: {{ form.monthly_response_balance || 0 }}

            <vcv-help-tooltip
              class="admin-company-rates__tooltip"
              text="Отклики на тарифах Startup и Business сгорают каждый месяц"
            />
          </div>

          <div
            v-show="!isResponsesUnlimited && (isCurrentTariff('professional') || isCurrentTariff('enterprise'))"
            key="1"
            class="admin-company-rates__block-inner"
          >
            <trigger
              v-model="isResponseBalanceEnabled"
              class="admin-company-rates__checkbox"
              small
              text="Разблокировать"
            />

            Отклики тарифа

            <vcv-billing-counter
              v-model="form.response_balance"
              :min-value="0"
              :max-value="100000"
              :disabled="!isResponseBalanceEnabled"
            />

            <vcv-help-tooltip
              class="admin-company-rates__tooltip"
              text="Данные отклики даются на весь период и не сгорают"
            />
          </div>

          <div v-show="!isResponsesUnlimited" key="2" class="admin-company-rates__block-inner">
            Дополнительные отклики

            <vcv-billing-counter v-model="form.extra_response_balance" :min-value="0" :max-value="100000" />

            <vcv-help-tooltip
              class="admin-company-rates__tooltip"
              text="Здесь можно добавить платных откликов клиенту вручную"
            />
          </div>
        </transition-group>

        <!-- <trigger
          v-model="form.save_no_paid_responses"
          class="admin-company-rates__checkbox"
          text="Сохранять отклики при неоплаченном тарифе"
          disabled
          small
          /> -->
      </div>

      <div class="admin-company-rates__block">
        <div class="admin-company-rates__title">Время хранения откликов</div>

        <div class="admin-company-rates__block-inner">
          <trigger v-model="isResponsesLifetimeUnlimited" class="admin-company-rates__checkbox" text="Безлимит" small />
        </div>

        <div v-show="!isResponsesLifetimeUnlimited" key="2" class="admin-company-rates__block-inner">
          Время хранения отклика

          <vcv-billing-counter v-model="form.responses_lifetime" :min-value="minResponsesLifetime" :max-value="60" />
        </div>
      </div>

      <vbutton text="Сохранить" @click="saveTariff()" />
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Axios from 'axios';
import Trigger from '@/components/shared/Trigger';
import DateRangePicker from 'vue2-daterange-picker';
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css';
import VcvBillingCounter from '@/routes_new/inner/billing/VcvBillingCounter';
import VcvHelpTooltip from '@/components/VcvHelpTooltip';

export default {
  name: 'AdminCompanyRates',

  components: {
    Trigger,
    VcvBillingCounter,
    DateRangePicker,
    VcvHelpTooltip
  },

  data() {
    return {
      dateRange: {
        startDate: null,
        endDate: null
      },

      form: null,
      tariffs: null,
      isVacanciesUnlimited: false,
      isResponsesUnlimited: false,
      isResponsesLifetimeUnlimited: false,
      isLoading: false,
      isLoaded: false,
      isMonthlyResponseEnabled: false,
      isResponseBalanceEnabled: false
    };
  },

  computed: {
    minResponsesLifetime() {
      const currentTariff = this.tariffs.find(x => x.id === this.form.tariff_id);

      if (currentTariff !== undefined) {
        if (currentTariff.name === 'business') {
          return 2;
        } else if (currentTariff.name === 'professional') {
          return 3;
        }
      }

      return 1;
    }
  },

  watch: {
    isVacanciesUnlimited(val) {
      if (val) {
        this.form.vacancy_limit = null;
      } else {
        this.form.vacancy_limit = 1;
      }
    },

    isResponsesUnlimited(val) {
      if (val) {
        this.form.response_limit = null;
      } else {
        this.form.response_limit = this.form.response_balance;
      }
    },

    isResponsesLifetimeUnlimited(val) {
      if (val) {
        this.form.responses_lifetime = null;
      } else {
        this.form.responses_lifetime = this.getCurrentTariff().responses_lifetime;
      }
    }
  },

  async created() {
    this.isLoading = true;

    await this.fetchCompany(this.$route.params.company_id);

    await Axios.get(`/v3/tariffs`).then(response => {
      this.tariffs = response.data._embedded.tariffs.sort((a, b) => a.id - b.id);
    });

    await Axios.get(`/v3/admin/company-limits/${this.$route.params.company_id}?fields[]=vacancyCount`).then(
      response => {
        // Заполняем все значения и переключатели из company-limits
        this.form = response.data;
        this.dateRange.startDate = response.data.paid_from;
        this.dateRange.endDate = response.data.paid_to;

        if (response.data.vacancy_limit === null) this.isVacanciesUnlimited = true;
        if (response.data.response_limit === null) this.isResponsesUnlimited = true;
        if (response.data.responses_lifetime === null) this.isResponsesLifetimeUnlimited = true;

        this.isLoaded = true;
      }
    );

    this.isLoading = false;
  },

  methods: {
    ...mapActions('admin_new/company', { fetchCompany: 'fetch' }),

    getCurrentTariff() {
      const currentTariff = this.tariffs.find(x => x.id === this.form.tariff_id);

      if (currentTariff !== undefined) {
        return currentTariff;
      }

      return null;
    },

    changeTariff(tariffId) {
      this.form.tariff_id = tariffId;

      this.fillTariffProperties(tariffId);
    },

    changeVacancyLimit(newVacancyLimit) {
      this.form.vacancy_limit = newVacancyLimit;

      this.fillTariffProperties(this.form.tariff_id, false); // without switches
    },

    fillTariffProperties(tariffId, withSwitches = true) {
      const tariff = this.tariffs.find(x => x.id == tariffId);

      if (tariff !== undefined) {
        // Свойства которые не заполняются в форму из /tariffs
        if (withSwitches) {
          const excludeProperties = ['id', '_links'];

          for (const key in tariff) {
            if (Object.hasOwnProperty.call(tariff, key) && !excludeProperties.includes(key)) {
              this.form[key] = tariff[key];
            }
          }
        }

        const days = this.$moment(this.dateRange.endDate).diff(this.dateRange.startDate, 'days');
        const months = days / 30;

        // Отклики несгораемые
        this.form.response_balance = Math.round(tariff.responses * (this.form.vacancy_limit || 1) * months);
        this.isResponsesUnlimited = false;

        // Отклики сгораемые (месячные отклики)
        this.form.monthly_response_limit = tariff.monthly_responses * (this.form.vacancy_limit || 1);
        this.form.monthly_response_balance = tariff.monthly_responses * (this.form.vacancy_limit || 1);

        // Время хранения откликов
        if (
          this.getCurrentTariff().responses_lifetime > this.form.responses_lifetime ||
          this.getCurrentTariff().responses_lifetime === null
        ) {
          this.form.responses_lifetime = this.getCurrentTariff().responses_lifetime;
        }

        if (this.getCurrentTariff().responses_lifetime === null) {
          this.isResponsesLifetimeUnlimited = true;
        }
      }
    },

    isCurrentTariff(tariffName) {
      const tariff = this.tariffs.find(x => x.name === tariffName);

      if (tariff !== undefined && tariff.id === this.form.tariff_id) {
        return true;
      }

      return false;
    },

    async saveTariff() {
      const params = Object.assign({}, params, this.form);
      params.paid_from =
        this.dateRange.startDate !== null ? this.$moment(this.dateRange.startDate).format() : this.$moment();
      params.paid_to = this.dateRange.endDate !== null ? this.$moment(this.dateRange.endDate).format() : this.$moment();

      await Axios.patch(`/v3/admin/company-limits/${this.$route.params.company_id}`, params)
        .then(() => {
          this.$noty.success('Изменения сохранены');
        })
        .catch(() => {
          this.$noty.warning('Изменения не сохранены');
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.admin-company-rates {
  &__preloader {
    padding: 30px 0;
  }

  &__header {
    @include mixin-text-header;
    font-size: 18px;
    margin-bottom: 20px;
  }

  &__title {
    @include mixin-text-header;
    margin-bottom: 10px;
  }

  &__checkbox {
    margin-bottom: 10px;

    ::v-deep .trigger__text {
      @include mixin-text-main;
    }
  }

  &__block {
    @include mixin-text-main;
    margin-bottom: 20px;

    ::v-deep tr {
      height: auto !important;
    }
  }

  &__block-inner {
    margin-bottom: 10px;
  }

  &__block-dates {
    display: flex;
    width: 500px;
  }

  &__tooltip {
    display: inline-block;
    margin: 0 5px;
  }
}
</style>
